import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import Layout from './components/Layout';
import MapComponent from './components/Map';
import ObjectsList from './components/ObjectsList';
import Login from './components/Login';
import Signup from './components/Signup';
import TestTools from './components/TestTools';
import ObjectManagement from './components/ObjectManagement';
import AuthenticatedRoute from './components/AuthenticatedRoute';
import GeofencePage from './components/GeofencePage';
import { AuthProvider } from './context/AuthContext';
import { TrackedObject, LocationPoint } from './types';
import objectsService, { LocationUpdate } from './services/ObjectsService';
import { mapServiceObjectsToAppObjects } from './utils/objectMappers';
import { eventEmitter, EVENTS } from './events/EventEmitter';
import webSocketService from './services/WebSocketService';

const App: React.FC = () => {
  const [objects, setObjects] = useState<TrackedObject[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedObjectId, setSelectedObjectId] = useState<string | undefined>(undefined);
  const [refreshingIds, setRefreshingIds] = useState<Set<string>>(new Set());

  // Fetch objects from service when component mounts
  useEffect(() => {
    const fetchObjects = async () => {
      try {
        setLoading(true);
        const serviceObjects = await objectsService.getAllObjects();
        
        // Create a map to store locations for each object
        const locationMap = new Map<string, LocationUpdate>();
        
        // Get latest locations for all objects
        await Promise.all(
          serviceObjects.map(async (obj) => {
            try {
              const location = await objectsService.getLatestLocation(obj.id);
              if (location) {
                locationMap.set(obj.id, location);
              }
            } catch (error) {
              console.error(`Failed to fetch location for object ${obj.id}:`, error);
            }
          })
        );
        
        // Convert service objects to app objects with their locations
        const trackedObjects = mapServiceObjectsToAppObjects(serviceObjects, locationMap);
        
        setObjects(trackedObjects);
      } catch (error) {
        console.error('Failed to fetch objects:', error);
        // Fallback to empty array if fetch fails
        setObjects([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchObjects();
    
    // Set up periodic refresh every minute
    const intervalId = setInterval(() => {
      fetchObjects();
    }, 60000);
    
    // Listen for location update events from local event system
    const unsubscribe = eventEmitter.subscribe(EVENTS.LOCATION_UPDATED, (updatedObject: TrackedObject) => {
      console.log('Received location update event:', updatedObject);
      setObjects(currentObjects => {
        // Find and update the object with new location data
        return currentObjects.map(obj => 
          obj.id === updatedObject.id ? updatedObject : obj
        );
      });
    });
    
    // Connect to WebSocket for real-time updates
    webSocketService.connect().then(() => {
      console.log('WebSocket connected for real-time updates');
    }).catch(error => {
      console.error('Failed to connect to WebSocket:', error);
    });
    
    // Clean up on unmount
    return () => {
      clearInterval(intervalId);
      unsubscribe();
      webSocketService.disconnect();
    };
  }, []);

  // Fetch the latest location for a specific object and maintain location history
  const fetchObjectLocation = useCallback(async (objectId: string) => {
    try {
      console.log(`Polling latest location for object: ${objectId}`);
      const location = await objectsService.getLatestLocation(objectId);
      
      if (location) {
        // Update the object in the state using functional update pattern
        // which doesn't require objects as a dependency
        setObjects(currentObjects => {
          // Find the current object to update
          const objectIndex = currentObjects.findIndex(obj => obj.id === objectId);
          if (objectIndex === -1) return currentObjects;
          
          const currentObject = currentObjects[objectIndex];
          
          // Create new location history, keeping the most recent 10 points
          const newPoint: LocationPoint = {
            latitude: location.latitude,
            longitude: location.longitude,
            timestamp: location.timestamp
          };
          
          // Get existing history or create empty array
          const existingHistory = currentObject.locationHistory || [];
          
          // Create new history array with the new point at the end
          // and limit to the latest 10 entries
          const newHistory = [...existingHistory, newPoint].slice(-10);
          
          // Create a new array with the updated object
          const newObjects = [...currentObjects];
          newObjects[objectIndex] = {
            ...newObjects[objectIndex],
            latitude: location.latitude,
            longitude: location.longitude,
            lastUpdate: location.timestamp,
            locationHistory: newHistory
          };
          
          console.log(`Updated location for ${objectId}: (${location.latitude}, ${location.longitude})`);
          console.log(`Location history now has ${newHistory.length} points`);
          return newObjects;
        });
      }
    } catch (error) {
      console.error(`Failed to poll location for object ${objectId}:`, error);
    }
  }, []); // No dependencies needed with functional update pattern
  
  // Poll for selected object's location every 5 seconds - ONLY using the interval
  useEffect(() => {
    // Only poll if we have a selected object
    if (!selectedObjectId) return;
    
    console.log('Setting up polling interval for selected object (exactly every 5 seconds)');
    
    // Set up polling interval (exactly every 5 seconds)
    // NO immediate fetch - strictly run once every 5 seconds
    const pollingIntervalId = setInterval(() => {
      console.log(`Polling object ${selectedObjectId} location at ${new Date().toLocaleTimeString()}`);
      fetchObjectLocation(selectedObjectId);
    }, 5000);
    
    // Clean up interval when component unmounts or selectedObjectId changes
    return () => {
      console.log('Cleaning up polling interval');
      clearInterval(pollingIntervalId);
    };
  }, [selectedObjectId, fetchObjectLocation]);

  // Handler for selecting an object
  const handleSelectObject = (id: string) => {
    setSelectedObjectId(id);
  };

  // Handler for on-demand location refresh from map sidebar
  const handleRefreshLocation = useCallback(async (id: string) => {
    setRefreshingIds(prev => new Set(prev).add(id));
    try {
      await objectsService.refreshObjectLocation(id);
      await fetchObjectLocation(id);
    } catch (error) {
      console.error(`Failed to refresh location for object ${id}:`, error);
    } finally {
      setRefreshingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [fetchObjectLocation]);

  return (
    <AuthProvider>
      <Router>
        <Layout>
          <Routes>
            {/* Public routes */}
            <Route 
              path="/login" 
              element={<Login />} 
            />
            <Route 
              path="/signup" 
              element={<Signup />} 
            />
            
            {/* Protected routes */}
            <Route 
              path="/" 
              element={
                <AuthenticatedRoute>
                  <div className="map-view">
                    <div className="sidebar">
                      <ObjectsList 
                        objects={objects} 
                        onSelectObject={handleSelectObject} 
                        selectedObjectId={selectedObjectId}
                        onRefresh={handleRefreshLocation}
                        refreshingIds={refreshingIds}
                      />
                    </div>
                    <div className="map-area">
                      <MapComponent 
                        objects={objects} 
                        selectedObjectId={selectedObjectId} 
                        onSelectObject={handleSelectObject}
                      />
                    </div>
                  </div>
                </AuthenticatedRoute>
              } 
            />
            <Route 
              path="/objects" 
              element={
                <AuthenticatedRoute>
                  <ObjectManagement />
                </AuthenticatedRoute>
              } 
            />
            <Route
              path="/geofences"
              element={
                <AuthenticatedRoute>
                  <GeofencePage />
                </AuthenticatedRoute>
              }
            />
            <Route
              path="/test-tools"
              element={
                <AuthenticatedRoute>
                  <TestTools />
                </AuthenticatedRoute>
              }
            />
            
            {/* Fallback route */}
            <Route 
              path="*" 
              element={<Navigate to="/" replace />} 
            />
          </Routes>
        </Layout>
      </Router>
    </AuthProvider>
  );
};

export default App;
